
import { useState, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useProject } from "@/hooks/useProject";

// Estados de diálogos agrupados
interface DialogState {
  edit: boolean;
  delete: boolean;
  add: boolean;
  editCode: boolean;
  deleteAll: boolean;
  qr: boolean;
}

// Estados de loading agrupados
interface LoadingState {
  addingCodes: boolean;
  deletingCode: boolean;
  editingCode: boolean;
  deletingAllCodes: boolean;
}

// Estado del QR agrupado
interface QrState {
  code: string;
  url: string;
}

export const useLaunchDetail = (launchId?: number) => {
  // Estados agrupados - reduce re-renders
  const [dialogs, setDialogs] = useState<DialogState>({
    edit: false,
    delete: false,
    add: false,
    editCode: false,
    deleteAll: false,
    qr: false,
  });

  const [loading, setLoading] = useState<LoadingState>({
    addingCodes: false,
    deletingCode: false,
    editingCode: false,
    deletingAllCodes: false,
  });

  const [qrState, setQrState] = useState<QrState>({
    code: "",
    url: "",
  });

  // Estados individuales que necesitan ser separados
  const [codeToDelete, setCodeToDelete] = useState<string | null>(null);
  const [numCodesToAdd, setNumCodesToAdd] = useState(1);
  const [editingCode, setEditingCode] = useState<{ code: string; title?: string }>({ code: "" });

  const queryClient = useQueryClient();
  const { projectClient, currentProject } = useProject();

  // Helper para actualizar un diálogo específico
  const setDialog = useCallback((key: keyof DialogState, value: boolean) => {
    setDialogs(prev => ({ ...prev, [key]: value }));
  }, []);

  // Helper para actualizar un estado de loading específico
  const setLoadingState = useCallback((key: keyof LoadingState, value: boolean) => {
    setLoading(prev => ({ ...prev, [key]: value }));
  }, []);

  // Query to fetch launch details (supports both legacy and V2)
  const { data: launch, isLoading: isLoadingLaunch } = useQuery({
    queryKey: ["launch", currentProject, launchId],
    queryFn: async () => {
      if (!launchId) throw new Error("ID de lanzamiento no válido");
      const { data, error } = await projectClient
        .from("form_launches")
        .select(`
          *,
          clientes (id, nombre),
          report_templates (id, name),
          jotform_form_mappings:form_mapping_id (id, jotform_form_id, form_title),
          report_templates_v2:template_v2_id (id, name)
        `)
        .eq("id", launchId)
        .eq('project_id', projectClient.getProjectId())
        .single();
      if (error) {
        toast.error("Error al cargar el lanzamiento");
        throw error;
      }
      return data;
    },
    enabled: !!launchId,
  });

  // Query to fetch codes for this launch
  const { data: codes, isLoading: isLoadingCodes } = useQuery({
    queryKey: ["launch_codes", currentProject, launchId],
    queryFn: async () => {
      if (!launchId) throw new Error("ID de lanzamiento no válido");
      const { data, error } = await projectClient
        .from("form_codes")
        .select("code")
        .eq("form_launch_id", launchId)
        .eq('project_id', projectClient.getProjectId())
        .order("created_at", { ascending: true });
      if (error) {
        toast.error("Error al cargar los códigos");
        throw error;
      }
      return data;
    },
    enabled: !!launchId,
  });

  const handleCopyAllCodes = useCallback(async () => {
    if (!codes) return;
    try {
      const allCodes = codes.map(code => code.code).join('\n');
      await navigator.clipboard.writeText(allCodes);
      toast.success("Todos los códigos copiados al portapapeles");
    } catch (error) {
      toast.error("Error al copiar los códigos");
    }
  }, [codes]);

  const handleCopyCode = useCallback(async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success("Código copiado al portapapeles");
    } catch (error) {
      toast.error("Error al copiar el código");
    }
  }, []);

  const handleCopyUrl = useCallback(async (code: string) => {
    try {
      const shortUrl = `${window.location.origin}/${code}`;
      await navigator.clipboard.writeText(shortUrl);
      toast.success("URL copiada al portapapeles");
    } catch (error) {
      toast.error("Error al copiar la URL");
    }
  }, []);

  const handleShowQr = useCallback((code: string) => {
    const shortUrl = `${window.location.origin}/${code}`;
    setQrState({ code, url: shortUrl });
    setDialog('qr', true);
  }, [setDialog]);

  const handleDownloadQrImage = useCallback(() => {
    try {
      const canvas = document.getElementById('qr-code-canvas') as HTMLCanvasElement;
      if (!canvas) {
        throw new Error("No se pudo encontrar el canvas del QR");
      }
      canvas.toBlob(blob => {
        if (!blob) {
          throw new Error("No se pudo convertir el QR a imagen");
        }
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `qr-codigo-${qrState.code}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        toast.success("Imagen QR descargada");
      }, 'image/png');
    } catch (error) {
      console.error("Error al descargar la imagen QR:", error);
      toast.error("Error al descargar la imagen QR");
    }
  }, [qrState.code]);

  const handleDeleteCode = useCallback(async () => {
    if (!codeToDelete || !launchId) return;
    setLoadingState('deletingCode', true);
    try {
      const { error } = await projectClient
        .from("form_codes")
        .delete()
        .eq("code", codeToDelete)
        .eq("form_launch_id", launchId)
        .eq('project_id', projectClient.getProjectId());
      if (error) throw error;
      toast.success("Código eliminado correctamente");
      queryClient.invalidateQueries({ queryKey: ["launch_codes", currentProject, launchId] });
    } catch (error) {
      console.error("Error al eliminar el código:", error);
      toast.error("Error al eliminar el código");
    } finally {
      setLoadingState('deletingCode', false);
      setDialog('delete', false);
      setCodeToDelete(null);
    }
  }, [codeToDelete, launchId, projectClient, queryClient, currentProject, setLoadingState, setDialog]);

  const generateThreeDigitCode = () => {
    return Math.floor(Math.random() * 900 + 100).toString();
  };

  const handleAddCodes = useCallback(async () => {
    if (!launchId || numCodesToAdd <= 0) return;
    setLoadingState('addingCodes', true);
    try {
      const newCodes = Array.from({ length: numCodesToAdd }, () => {
        const code = generateThreeDigitCode();
        return {
          code,
          form_launch_id: launchId,
          title: code,
          project_id: projectClient.getProjectId(),
        };
      });
      const { error } = await projectClient.from("form_codes").insert(newCodes);
      if (error) throw error;
      toast.success(`${numCodesToAdd} código(s) añadido(s) correctamente`);
      queryClient.invalidateQueries({ queryKey: ["launch_codes", currentProject, launchId] });
      setDialog('add', false);
      setNumCodesToAdd(1);
    } catch (error) {
      console.error("Error al añadir códigos:", error);
      toast.error("Error al añadir códigos");
    } finally {
      setLoadingState('addingCodes', false);
    }
  }, [launchId, numCodesToAdd, projectClient, queryClient, currentProject, setLoadingState, setDialog]);

  const handleEditCode = useCallback((code: string, title: string | null) => {
    setEditingCode({ code, title: title || code });
    setDialog('editCode', true);
  }, [setDialog]);

  const handleSaveEditCode = useCallback(async () => {
    toast.info("Los códigos no tienen título en esta versión");
    setDialog('editCode', false);
  }, [setDialog]);

  const handleDeleteAllCodes = useCallback(async () => {
    if (!launchId) return;
    setLoadingState('deletingAllCodes', true);
    try {
      const { error } = await projectClient
        .from("form_codes")
        .delete()
        .eq("form_launch_id", launchId)
        .eq('project_id', projectClient.getProjectId());
      if (error) throw error;
      toast.success("Todos los códigos eliminados correctamente");
      queryClient.invalidateQueries({ queryKey: ["launch_codes", currentProject, launchId] });
    } catch (error) {
      console.error("Error al eliminar todos los códigos:", error);
      toast.error("Error al eliminar todos los códigos");
    } finally {
      setLoadingState('deletingAllCodes', false);
      setDialog('deleteAll', false);
    }
  }, [launchId, projectClient, queryClient, currentProject, setLoadingState, setDialog]);

  // Calculate report lifetime in days
  const reportLifetimeDays = launch?.report_lifetime
    ? Math.round(launch.report_lifetime / (24 * 60 * 60 * 1000))
    : null;

  return {
    // Data
    launch,
    codes,
    isLoadingLaunch,
    isLoadingCodes,
    reportLifetimeDays,

    // Dialog states - mantiene compatibilidad con API existente
    isEditDialogOpen: dialogs.edit,
    setIsEditDialogOpen: (value: boolean) => setDialog('edit', value),
    isDeleteDialogOpen: dialogs.delete,
    setIsDeleteDialogOpen: (value: boolean) => setDialog('delete', value),
    isAddDialogOpen: dialogs.add,
    setIsAddDialogOpen: (value: boolean) => setDialog('add', value),
    isEditCodeDialogOpen: dialogs.editCode,
    setIsEditCodeDialogOpen: (value: boolean) => setDialog('editCode', value),
    isDeleteAllDialogOpen: dialogs.deleteAll,
    setIsDeleteAllDialogOpen: (value: boolean) => setDialog('deleteAll', value),
    isQrDialogOpen: dialogs.qr,
    setIsQrDialogOpen: (value: boolean) => setDialog('qr', value),

    // Loading states
    isAddingCodes: loading.addingCodes,
    isDeletingCode: loading.deletingCode,
    isEditingCode: loading.editingCode,
    isDeletingAllCodes: loading.deletingAllCodes,

    // Form values
    codeToDelete,
    setCodeToDelete,
    numCodesToAdd,
    setNumCodesToAdd,
    editingCode,
    setEditingCode,
    currentQrCode: qrState.code,
    currentQrUrl: qrState.url,

    // Handlers
    handleCopyAllCodes,
    handleCopyCode,
    handleCopyUrl,
    handleShowQr,
    handleDownloadQrImage,
    handleDeleteCode,
    handleAddCodes,
    handleEditCode,
    handleSaveEditCode,
    handleDeleteAllCodes,
  };
};
